import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useCompanyEmployeeCount } from "../hooks/useCompanyEmployeeCount";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import {
  TransformedDataPoint,
  calculateAnnualGrowthMetrics,
} from "../utils/chartDataTransformers";
import { DEFAULT_ANNUAL_LIMIT, MAX_ANNUAL_LIMIT, safeSlice } from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import GrowthMetricsDisplay from "./shared/GrowthMetricsDisplay";
import ChartTooltip from "./shared/ChartTooltip";

interface EmployeeCountChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  limit?: number;
}

const EmployeeCountChart: React.FC<EmployeeCountChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode === "dark");

  const effectiveLimit = limitProp ?? DEFAULT_ANNUAL_LIMIT;

  const { data, isLoading, error } = useCompanyEmployeeCount(
    ticker,
    MAX_ANNUAL_LIMIT
  );

  const chartData = useMemo((): TransformedDataPoint[] => {
    if (!data || data.length === 0) return [];

    const validData = data.filter(
      (item) => item.employeeCount !== null && item.employeeCount > 0
    );

    const result = validData
      .sort(
        (a, b) =>
          dayjs(a.periodOfReport).unix() - dayjs(b.periodOfReport).unix()
      )
      .map((item) => ({
        period: dayjs(item.periodOfReport).year().toString(),
        employeeCount: item.employeeCount,
        date: item.periodOfReport,
        year: dayjs(item.periodOfReport).year(),
        quarter: null,
      }));

    return safeSlice(result, effectiveLimit);
  }, [data, effectiveLimit]);

  const growthMetrics = useMemo(() => {
    if (chartData.length < 2) return [];
    return calculateAnnualGrowthMetrics(chartData, "employeeCount");
  }, [chartData]);

  return (
    <GenericFinancialBarChart
      title="Employee Count Growth"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode="annual"
      barKeys={["employeeCount"]}
      colors={[colors.positive]}
      yAxisLegend="Employee Count"
      yAxisFormat={(value) => formatNumber(value)}
      noDataTitle="No Employee Count Data Available"
      noDataDescription="Employee count data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
      tooltipContent={({ indexValue, value, color }) => (
        <ChartTooltip
          header={String(indexValue)}
          headerColor={color}
          rows={[{ label: "Employees", value: formatNumber(value as number, 2) }]}
        />
      )}
      growthMetrics={
        <GrowthMetricsDisplay metrics={growthMetrics} />
      }
    />
  );
};

export default EmployeeCountChart;
